import { Dispatch, SetStateAction } from 'react';
import { toast } from 'react-toastify';

import { CustomSelectOptionType } from './types';
import {
    isEmail,
    isRequiredChars,
    isNumericSpecialChars,
    isRepeatedChars
} from './common';
import { JoinErrors } from '@pages/Auth/Join';
import { ProfileErrors } from '@pages/Profile';

type InputsType = Record<string, string>;
type OverlapsType = Record<string, boolean>;

// 이메일 찾기
export const validateFindEmail = (inputs: InputsType) => {
    const { license, name } = inputs;
    let validity = true;

    if (!license || license.length < 8) {
        toast.warning('요양기관번호를 정확히 입력해주세요');
        validity = false;
    } else if (!name) {
        toast.warning('대표자명을 입력해주세요');
        validity = false;
    }

    return validity;
};
// 비밀번호 찾기
export const validateFindPw = (
    inputs: InputsType,
    verifyCellphone: boolean
) => {
    const { email, name, license, cellphone, verifyCode } = inputs;
    let validity = true;

    if (!isEmail(email)) {
        toast.warning('이메일을 정확히 입력해주세요');
        validity = false;
    } else if (!license || license.length < 8) {
        toast.warning('요양기관번호를 정확히 입력해주세요');
        validity = false;
    } else if (!name) {
        toast.warning('대표자명을 입력해주세요');
        validity = false;
    }

    return validity;
};
// 비밀번호 변경
export const validateChangePw = (firstLogin: boolean, inputs: InputsType) => {
    const { currentPw, newPw, confirmPw } = inputs;
    let validity = true;

    if (!firstLogin && !currentPw) {
        toast.warning('현재 비밀번호를 입력해주세요.');
        validity = false;
    } else if (!newPw) {
        toast.warning('변경할 비밀번호를 입력해주세요.');
        validity = false;
    } else if (!confirmPw) {
        toast.warning('비밀번호 확인을 입력해주세요.');
        validity = false;
    }
    return validity;
};
// 비밀번호 형식
export const validatePw = (pw: string) => {
    if (pw.length < 8) {
        return false;
    } else if (!isRequiredChars(pw)) {
        return false;
    } else if (!isNumericSpecialChars(pw)) {
        return false;
    } else if (isRepeatedChars(pw)) {
        return false;
    }
    return true;
};
// 회원가입
export const validateJoin = (
    inputs: InputsType,
    overlaps: OverlapsType,
    hasPhoneNum: boolean,
    hasFaxNum: boolean,
    setErrors: Dispatch<SetStateAction<JoinErrors>>
) => {
    const {
        email,
        emailAuthNum,
        username,
        license,
        hospitalName,
        password,
        passwordConfirm,
        phoneNum1,
        phoneNum2,
        phoneNum3,
        faxNum1,
        faxNum2,
        faxNum3,
        address,
        chart
    } = inputs;

    const newErrors: JoinErrors = {};

    if (!isEmail(email) || !overlaps.email) {
        newErrors.email = '이메일을 확인해주세요.';
    } else if (emailAuthNum.length !== 6 || !overlaps.emailAuthNum) {
        newErrors.emailAuthNum = '인증번호를 확인해주세요.';
    } else if (!username) {
        newErrors.username = '이름을 확인해주세요.';
    } else if (license.length !== 8 || !overlaps.license) {
        newErrors.license = '요양기관 번호를 확인해주세요';
    } else if (!hospitalName) {
        newErrors.hospitalName = '병원명을 확인해주세요';
    } else if (!validatePw(password)) {
        newErrors.password = '비밀번호를 확인해주세요';
    } else if (password !== passwordConfirm) {
        newErrors.passwordConfirm = '비밀번호 재입력을 확인해주세요';
    } else if (!hasPhoneNum && phoneNum1.length < 2) {
        newErrors.phoneNum1 = '전화번호를 확인해주세요';
    } else if (!hasPhoneNum && phoneNum2.length < 3) {
        newErrors.phoneNum2 = '전화번호를 확인해주세요';
    } else if (!hasPhoneNum && phoneNum3.length !== 4) {
        newErrors.phoneNum3 = '전화번호를 확인해주세요';
    } else if (!hasFaxNum && faxNum1.length < 2) {
        newErrors.faxNum1 = '팩스 번호를 확인해주세요';
    } else if (!hasFaxNum && faxNum2.length < 3) {
        newErrors.faxNum2 = '팩스 번호를 확인해주세요';
    } else if (!hasFaxNum && faxNum3.length !== 4) {
        newErrors.faxNum3 = '팩스 번호를 확인해주세요';
    } else if (!address) {
        newErrors.address = '주소를 확인해주세요';
    } else if (chart === '차트 선택') {
        newErrors.chart = '차트를 선택해주세요';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
};

export const validateDidBasicSetting = (
    inputs: InputsType,
    writtenType: CustomSelectOptionType | null
) => {
    const { usePatientChart, useCallingPati, useWeatherAni } = inputs;
    let validity = true;

    if (!usePatientChart) {
        toast.warning('대기환자 노출 섹션 사용 여부를 선택해주세요.');
        validity = false;
    } else if (!useCallingPati) {
        toast.warning('환자 호출 사용 여부를 선택해주세요.');
        validity = false;
    } else if (!writtenType) {
        toast.warning('진료실명 표기 방법을 선택해주세요.');
        validity = false;
    } else if (!useWeatherAni) {
        toast.warning('날씨 애니메이션 사용 여부를 선택해주세요.');
        validity = false;
    }
    return validity;
};

export const activateCreateCampaign = (
    inputs: InputsType,
    file: File | null,
    regionTags: string[],
    medicalDepartments: number[],
    hospitalTags: string[]
) => {
    const {
        resolution,
        title,
        contractMethod,
        useTarget,
        transmissionDigit,
        transmissionAmount,
        advertisingFee,
        useRegionSort,
        useDesignation
    } = inputs;
    let validity = false;

    if (!file) {
        validity = true;
    } else if (!resolution) {
        validity = true;
    } else if (!title) {
        validity = true;
    } else if (contractMethod === '계약방식 선택') {
        validity = true;
    } else if (!useTarget) {
        validity = true;
    } else if (useTarget === 'true' && transmissionDigit === '단위 선택') {
        validity = true;
    } else if (useTarget === 'true' && !transmissionAmount) {
        validity = true;
    } else if (!advertisingFee) {
        validity = true;
    } else if (!useRegionSort) {
        validity = true;
    } else if (useRegionSort === 'true' && !regionTags.length) {
        validity = true;
    } else if (!medicalDepartments.length) {
        validity = true;
    } else if (!useDesignation) {
        validity = true;
    } else if (useDesignation === 'true' && !hospitalTags.length) {
        validity = true;
    }

    return validity;
};
// 내 프로필
export const validateProfile = (
    inputs: InputsType,
    hasPhoneNum: boolean,
    hasFaxNum: boolean,
    setErrors: Dispatch<SetStateAction<ProfileErrors>>
) => {
    const {
        username,
        password,
        hospitalName,
        phoneNum1,
        phoneNum2,
        phoneNum3,
        faxNum1,
        faxNum2,
        faxNum3,
        address,
        chart
    } = inputs;

    const newErrors: ProfileErrors = {};

    if (!username) {
        newErrors.username = '이름을 확인해주세요.';
    } else if (!hospitalName) {
        newErrors.hospitalName = '병원명을 확인해주세요';
    } else if (!hasPhoneNum && phoneNum1.length < 2) {
        newErrors.phoneNum1 = '전화번호를 확인해주세요';
    } else if (!hasPhoneNum && phoneNum2.length < 3) {
        newErrors.phoneNum2 = '전화번호를 확인해주세요';
    } else if (!hasPhoneNum && phoneNum3.length !== 4) {
        newErrors.phoneNum3 = '전화번호를 확인해주세요';
    } else if (!hasFaxNum && faxNum1.length < 2) {
        newErrors.faxNum1 = '팩스 번호를 확인해주세요';
    } else if (!hasFaxNum && faxNum2.length < 3) {
        newErrors.faxNum2 = '팩스 번호를 확인해주세요';
    } else if (!hasFaxNum && faxNum3.length !== 4) {
        newErrors.faxNum3 = '팩스 번호를 확인해주세요';
    } else if (!address) {
        newErrors.address = '주소를 확인해주세요';
    } else if (chart === '차트 선택') {
        newErrors.chart = '차트를 선택해주세요';
    } else if (!password) {
        newErrors.password = '비밀번호를 확인해주세요';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
};
